/**
 * Author helpers for share-canvas comments (display + auth-facing normalizers).
 */

/** Clerk user: primary email lowercased, or null if missing. */
export function shareCommentClerkPrimaryEmailLower(
  user:
    | {
        primaryEmailAddress?: { emailAddress?: string | null } | null;
      }
    | null
    | undefined,
): string | null {
  const e = user?.primaryEmailAddress?.emailAddress?.trim().toLowerCase();
  return e && e.length > 0 ? e : null;
}

/**
 * /p/[token] comment identity: when signed in, use Clerk email; otherwise session email from the store.
 */
export function resolveShareViewerAuthorEmail(args: {
  storeViewerEmail: string | null | undefined;
  user:
    | {
        primaryEmailAddress?: { emailAddress?: string | null } | null;
      }
    | null
    | undefined;
  isSignedIn: boolean;
}): string | null {
  if (args.isSignedIn) {
    const clerk = shareCommentClerkPrimaryEmailLower(args.user);
    if (clerk) return clerk;
  }
  const s = args.storeViewerEmail?.trim();
  if (s) return normalizeShareCommentEmail(s);
  return null;
}

/** Normalize for anonymous viewer email comparison. */
export function normalizeShareCommentEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Safe display label derived from email local-part; avoids exposing full raw email publicly. */
export function shareCommentAuthorLabelFromEmail(rawEmail: string): string {
  const value = rawEmail.trim();
  if (!value) return "Unknown";
  if (!value.includes("@")) return value;
  const local = value.split("@")[0] ?? value;
  const clean = local.replace(/[._-]+/g, " ").trim();
  if (!clean) return "Unknown";
  return clean
    .split(/\s+/)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
}

/**
 * Display label for the current viewer's own (optimistic) comments: prefer the
 * Clerk profile name, fall back to the email-derived label (LEV-941). Matches
 * the server-side preference in `mapPublicCommentRow` / `toRecord`, so the
 * optimistic row doesn't flash "Dan+user2" before settling on "Joe".
 */
export function shareCommentViewerAuthorLabel(args: {
  fullName: string | null | undefined;
  email: string | null | undefined;
}): string {
  const name = args.fullName?.trim();
  if (name) return name;
  const email = args.email?.trim();
  return email ? shareCommentAuthorLabelFromEmail(email) : "";
}

/** Stable non-PII author seed (for avatar color) derived from normalized email. */
export function shareCommentAuthorSeedFromEmail(rawEmail: string): string {
  const s = normalizeShareCommentEmail(rawEmail);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `author-${(h >>> 0).toString(16).padStart(8, "0")}`;
}

/** Anonymous session may drag only threads they started (root row email). */
export function shareCommentViewerIsRootAuthor(
  viewerEmail: string | null | undefined,
  rootCommentEmail: string,
): boolean {
  if (!viewerEmail?.trim()) return false;
  return (
    normalizeShareCommentEmail(viewerEmail) ===
    normalizeShareCommentEmail(rootCommentEmail)
  );
}

/**
 * Logged-in user may drag a thread only if they match the root comment `email` field
 * (same rules as addMemberCanvasCommentReply when storing owner identity).
 */
export function shareCommentDbUserIsRootAuthor(args: {
  userEmail: string | null | undefined;
  userName: string | null | undefined;
  userId: string;
  rootCommentEmail: string;
}): boolean {
  const ce = args.rootCommentEmail.trim();
  const nec = normalizeShareCommentEmail(ce);
  const ue = args.userEmail?.trim().toLowerCase();
  if (ue && ue === nec) return true;
  const un = args.userName?.trim();
  if (un && un === ce.trim()) return true;
  if (args.userId === ce) return true;
  return false;
}

/** Client-side hint using Clerk profile (server still enforces DB user). */
export function shareCommentClerkUserIsRootAuthor(args: {
  primaryEmail: string | null | undefined;
  fullName: string | null | undefined;
  userId: string;
  rootCommentEmail: string;
}): boolean {
  return shareCommentDbUserIsRootAuthor({
    userEmail: args.primaryEmail,
    userName: args.fullName,
    userId: args.userId,
    rootCommentEmail: args.rootCommentEmail,
  });
}
